// src/services/sesionPOSService.ts
import { axiosInstance } from '../axiosConfig';
import { handleRequestWithAuth } from '../refreshService';
import { Cita, fetchCitaById } from '../citasService';
import { customerName, paymentCustomerName } from '../../utils/posSessionReport';
import { operatorHeaders, requireOperatorToken } from './operatorGrant';

const API_URL = 'sesiones-pos/';

// Interfaz para SesionPOS
export interface SesionPOS {
    id: number;
    punto_de_venta: number;
    usuario: number;
    fecha_apertura: string;
    fecha_cierre: string | null;
    saldo_inicial: number;
    saldo_actual: number; 
    saldo_final: number | null;
    estado: 'abierta' | 'cerrada' | 'cancelada';
    observaciones_apertura: string;
    observaciones_cierre: string;
    codigo: string | null; // NUEVO CAMPO
    usuario_nombre?: string;
    punto_de_venta_nombre?: string;
    sucursal_nombre?: string;
    reservas_count?: number;
    pagos_count?: number;
    total_pagos?: string;
    totales_por_categoria?: { efectivo: string; banco: string; otro: string };
    categorias_pago?: Array<{
        categoria: 'efectivo' | 'banco' | 'otro';
        cantidad: number;
        monto: string;
    }>;
    tipos_pago?: Array<{
        id: number;
        nombre: string;
        categoria: 'efectivo' | 'banco' | 'otro';
        cantidad: number;
        monto: string;
    }>;
}

export interface SesionPOSLookup {
    id: number;
    codigo: string | null;
    estado: SesionPOS['estado'];
    punto_de_venta: number;
    punto_de_venta_nombre: string;
    sucursal_nombre: string;
}

export interface SesionActivaResponse {
    id: number;
    punto_de_venta: number;
    usuario: number;
    fecha_apertura: string;
    fecha_cierre: string | null;
    saldo_inicial: number;
    saldo_final: number | null;
    saldo_actual: number;
    estado: 'abierta' | 'cerrada' | 'cancelada';
    observaciones_apertura: string;
    observaciones_cierre: string;
    codigo: string | null; // NUEVO CAMPO
    usuario_nombre?: string;
    punto_de_venta_nombre?: string;
    sucursal_nombre?: string;
}

export interface OperadorPOS {
    id: number;
    nombre: string;
    foto_url?: string | null;
    pin_configurado: boolean;
}

export interface OperadorPOSGrant {
    token: string;
    operador: Pick<OperadorPOS, 'id' | 'nombre'> & { foto_url?: string | null };
}

// Interfaz para abrir una nueva sesión
export interface AbrirSesionData {
    punto_de_venta_id: number;
    saldo_inicial: number;
    observaciones?: string;
}

// Obtener todas las sesiones POS
export const fetchSesionesPOS = async (): Promise<SesionPOS[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(API_URL);
        const sesionesData = response.data;
        
        // Convertir valores numéricos y asegurar codigo
        return sesionesData.map((sesion: any) => ({
            ...sesion,
            saldo_inicial: Number(sesion.saldo_inicial),
            saldo_actual: Number(sesion.saldo_actual),
            saldo_final: sesion.saldo_final != null && sesion.saldo_final !== '' ? Number(sesion.saldo_final) : null,
            codigo: sesion.codigo || null // Asegurar que codigo exista
        }));
    });
};

// Obtener una sesión POS por ID
export const fetchSesionPOS = async (id: number): Promise<SesionPOS> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(`${API_URL}${id}/`);
        const sesionData = response.data;
        
        // Convertir valores numéricos
        return {
            ...sesionData,
            saldo_inicial: Number(sesionData.saldo_inicial),
            saldo_actual: Number(sesionData.saldo_actual),
            saldo_final: sesionData.saldo_final != null && sesionData.saldo_final !== '' ? Number(sesionData.saldo_final) : null,
            codigo: sesionData.codigo || null // Asegurar que codigo exista
        };
    });
};



// Abrir una nueva sesión POS
export const abrirSesionPOS = async (data: AbrirSesionData): Promise<SesionPOS> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.post(`${API_URL}abrir_sesion/`, data);
        const sesionData = response.data;
        
        return {
            ...sesionData,
            saldo_inicial: Number(sesionData.saldo_inicial),
            saldo_actual: Number(sesionData.saldo_actual),
            saldo_final: sesionData.saldo_final != null && sesionData.saldo_final !== '' ? Number(sesionData.saldo_final) : null,
            codigo: sesionData.codigo || null // Asegurar que codigo exista
        };
    });
};

export const fetchOperadoresPOS = async (sesionId: number): Promise<OperadorPOS[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<OperadorPOS[]>(`${API_URL}${sesionId}/operadores/`);
        return response.data;
    });
};

export const activarOperadorPOS = async (
    sesionId: number,
    usuarioId: number,
    pin: string,
): Promise<OperadorPOSGrant> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.post<OperadorPOSGrant>(
            `${API_URL}${sesionId}/activar_operador/`,
            { usuario_id: usuarioId, pin },
        );
        return response.data;
    });
};

// Cerrar una sesión POS
export const cerrarSesionPOS = async (
    id: number, 
    data: {
        saldo_final: number;
        observaciones?: string;
    }
): Promise<SesionPOS> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.post(`${API_URL}${id}/cerrar_sesion/`, data);
        const sesionData = response.data;
        
        return {
            ...sesionData,
            saldo_inicial: Number(sesionData.saldo_inicial),
            saldo_actual: Number(sesionData.saldo_actual),
            saldo_final: sesionData.saldo_final != null && sesionData.saldo_final !== '' ? Number(sesionData.saldo_final) : null,
            codigo: sesionData.codigo || null // Asegurar que codigo exista
        };
    });
};


// Actualizar una sesión POS existente
export const updateSesionPOS = async (
    id: number,
    data: {
        observaciones_apertura?: string;
        observaciones_cierre?: string;
        saldo_final?: number;
        estado?: 'abierta' | 'cerrada' | 'cancelada';
    }
): Promise<SesionPOS> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.put(`${API_URL}${id}/`, data);
        return response.data;
    });
};

// Eliminar una sesión POS
export const deleteSesionPOS = async (id: number): Promise<void> => {
    return handleRequestWithAuth(async () => {
        await axiosInstance.delete(`${API_URL}${id}/`);
    });
};

// Registrar un movimiento de caja sobre la caja del URL; exige el grant del
// operador (``X-POS-Operator-Token``), que atribuye el asiento.
export const registrarMovimientoCaja = async (data: {
    sesion_id: number;
    tipo: 'entrada' | 'salida';
    monto: number;
    descripcion: string;
}, operadorToken: string | null | undefined): Promise<any> => {
    const config = operatorHeaders(requireOperatorToken(operadorToken));
    const { sesion_id, ...payload } = data;
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.post(`${API_URL}${sesion_id}/registrar_movimiento/`, payload, config);
        return response.data;
    });
};

// Obtener movimientos de una sesión
export const obtenerMovimientosSesion = async (sesionId: number): Promise<any[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(`${API_URL}${sesionId}/movimientos/`);
        return response.data;
    });
};

export const verificarSesionActiva = async (): Promise<boolean> => {
    return handleRequestWithAuth(async () => {
        try {
            const response = await axiosInstance.get(`${API_URL}verificar_sesion_activa/`);
            return response.data.tiene_sesion_activa;
        } catch (error) {
            /* Browser diagnostics omitted. */
            return false;
        }
    });
};


/**
 * Revalida por ID la sesión POS que la interfaz muestra como vigente.
 *
 * El backend exige que sea propia, abierta, con punto de venta activo y
 * asignado al usuario y sucursal activa (404/409 en caso contrario). Nunca
 * resuelve "otra" sesión: sin id no se llama al servidor.
 */
export const obtenerSesionActivaPorId = async (id: number): Promise<SesionActivaResponse> => {
    if (id === null || id === undefined || !Number.isInteger(Number(id))) {
        throw new Error('Se requiere la sesión POS explícita (id) para validarla.');
    }
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(`${API_URL}${id}/activa/`);
        const sesionData = response.data;
        return {
            ...sesionData,
            saldo_inicial: Number(sesionData.saldo_inicial),
            saldo_actual: Number(sesionData.saldo_actual),
            saldo_final: sesionData.saldo_final != null && sesionData.saldo_final !== '' ? Number(sesionData.saldo_final) : null,
            codigo: sesionData.codigo || null,
        };
    });
};

// Endpoint singular: SOLO para descubrir la sesión inicial del contexto POS.
// Con varias cajas abiertas el servidor responde 409; toda operación
// posterior debe usar el id explícito (``obtenerSesionActivaPorId``).
export const obtenerSesionActiva = async (): Promise<SesionActivaResponse> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(`${API_URL}sesion_activa/`);
        const sesionData = response.data;
        
        return {
            ...sesionData,
            saldo_inicial: Number(sesionData.saldo_inicial),
            saldo_actual: Number(sesionData.saldo_actual),
            saldo_final: sesionData.saldo_final != null && sesionData.saldo_final !== '' ? Number(sesionData.saldo_final) : null,
            codigo: sesionData.codigo || null // Asegurar que codigo exista
        };
    });
};
// Carga mínima para Registrar código: solo cajas abiertas y sin serializar
// movimientos, pagos ni citas históricas.
export const fetchSesionesAbiertasPorUsuario = async (): Promise<SesionPOSLookup[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(`${API_URL}usuario_sesiones_abiertas/`);
        return response.data || [];
    });
};

// Modificar la función fetchSesionesPorUsuario para aceptar un parámetro opcional
export const fetchSesionesPorUsuario = async (puntoDeVentaId?: number): Promise<SesionPOS[]> => {
    return handleRequestWithAuth(async () => {
        const url = puntoDeVentaId 
            ? `${API_URL}usuario_sesiones/?punto_de_venta=${puntoDeVentaId}`
            : `${API_URL}usuario_sesiones/`;
            
        const response = await axiosInstance.get(url);
        return response.data.map((sesion: any) => ({
            ...sesion,
            saldo_inicial: Number(sesion.saldo_inicial),
            saldo_actual: Number(sesion.saldo_actual),
            saldo_final: sesion.saldo_final != null && sesion.saldo_final !== '' ? Number(sesion.saldo_final) : null,
        }));
    });
};

// Agregar en sesionPOSService.ts
export const fetchCitasPorSesion = async (sesionId: number): Promise<Cita[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(`${API_URL}${sesionId}/citas/`);
        return response.data;
    });
};


export const fetchSesionesPorPuntoDeVenta = async (puntoDeVentaId: number): Promise<SesionPOS[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(`${API_URL}?punto_de_venta=${puntoDeVentaId}`);
        return response.data.map((sesion: any) => ({
            ...sesion,
            saldo_inicial: Number(sesion.saldo_inicial),
            saldo_actual: Number(sesion.saldo_actual),
            saldo_final: sesion.saldo_final != null && sesion.saldo_final !== '' ? Number(sesion.saldo_final) : null,
        }));
    });
};


export const fetchReporteSesionPOS = async (sesionId: number): Promise<any> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(`sesiones-pos/${sesionId}/reporte/`);
        const data = response.data;
        const bookings = data.citas || [];
        const sales = data.ventas_cafeteria || [];
        const missing = new Map<number, any[]>();
        const pagos = (data.pagos || []).map((payment: any) => {
            const item = { ...payment };
            const name = paymentCustomerName(item, bookings, sales);
            if (name) item.cliente_nombre = name;
            else {
                const id = Number((typeof item.cita === 'object' ? item.cita?.id : item.cita) || item.cita_info?.id);
                if (Number.isInteger(id) && id > 0) {
                    const items = missing.get(id) || [];
                    items.push(item); missing.set(id, items);
                }
            }
            return item;
        });
        // Reservas de otras sesiones o respuestas antiguas del reporte:
        // una consulta por reserva, máximo cuatro solicitudes simultáneas.
        const pending = Array.from(missing.entries());
        const resolveMissing = async () => {
            while (pending.length) {
                const [id, payments] = pending.shift()!;
                try {
                    const reservation = await fetchCitaById(id);
                    const name = customerName(reservation);
                    if (name) payments.forEach(payment => { payment.cliente_nombre = name; });
                } catch {
                    // No sustituir al cliente por el operador ni por otro usuario.
                }
            }
        };
        await Promise.all(Array.from({ length: Math.min(4, pending.length) }, resolveMissing));
        return { ...data, pagos };
    });
};
